import React, { useEffect, useState } from 'react';
import { Card, Upload, Button, Typography, message, Space, Switch, Select } from 'antd';
import { UploadOutlined, ShopOutlined } from '@ant-design/icons';
import axios from 'axios';
import { uploadTenantLogo, saveTenantLogo, getTenantBranding, syncPayrollBrandingLogo } from '../../services/tenantBrandingService';
import {
  DEFAULT_SESSION_PREFS, IDLE_TIMEOUT_CHOICES, normalizeSessionPrefs, setSessionPrefs,
  type SessionPrefs,
} from '../../lib/sessionKeepAlive';

const { Title, Paragraph, Text } = Typography;

/** Payslip PDFs embed raster images only. Convert SVG/WebP (and anything not
 *  PNG/JPG/GIF) to a PNG client-side so one asset works in the app and on the
 *  payslip; transparency is preserved for the white document background. */
async function toRasterLogo(file: File): Promise<File> {
  if (/(png|jpe?g|gif)$/i.test(file.type)) return file;
  const src = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = src;
    });
    const maxW = 600;
    const natW = img.naturalWidth || maxW;
    const natH = img.naturalHeight || Math.round(maxW / 3);
    const scale = natW > maxW ? maxW / natW : 1;
    const w = Math.max(1, Math.round(natW * scale));
    const h = Math.max(1, Math.round(natH * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
    const blob: Blob = await new Promise((res) => canvas.toBlob((b) => res(b!), 'image/png'));
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.png', { type: 'image/png' });
  } finally {
    URL.revokeObjectURL(src);
  }
}

// Session & activity — the signed-in OPERATOR's own preference (not tenant
// config): keep the session alive while active and/or auto sign-out after
// inactivity. Stored in auth-service user preferences (userMetadata.session)
// so it follows the user across KaziPeople apps; mirrored to the local
// keep-alive cache so it applies immediately.
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

const SessionPrefsCard: React.FC = () => {
  const [prefs, setPrefs] = useState<SessionPrefs>({ ...DEFAULT_SESSION_PREFS });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_URL}/users/profile`)
      .then((r) => {
        if (cancelled) return;
        const p = normalizeSessionPrefs(r.data?.userMetadata?.session);
        setPrefs(p);
        setSessionPrefs(p);
      })
      .catch(() => undefined)
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  const save = async (next: SessionPrefs) => {
    const prev = prefs;
    setPrefs(next);
    setSessionPrefs(next);
    try {
      await axios.put(`${API_URL}/users/profile/preferences`, { session: next });
      message.success('Session preference saved.');
    } catch {
      setPrefs(prev);
      setSessionPrefs(prev);
      message.error('Could not save the session preference.');
    }
  };

  const idleLabel = (m: number) =>
    m === 0 ? 'Never — stay signed in' : m < 60 ? `${m} minutes of inactivity` : `${m / 60} hour(s) of inactivity`;

  return (
    <Card title="Session & activity" style={{ marginTop: 16 }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space align="start" style={{ justifyContent: 'space-between', width: '100%', display: 'flex' }}>
          <div>
            <Text strong>Stay signed in while I&rsquo;m active</Text>
            <div><Text type="secondary" style={{ fontSize: 12 }}>
              Quietly renews your session while you&rsquo;re using the console, so you aren&rsquo;t signed out mid-task.
            </Text></div>
          </div>
          <Switch
            disabled={!loaded}
            checked={prefs.keepActive}
            onChange={(v) => save({ ...prefs, keepActive: v })}
          />
        </Space>
        <div>
          <Text strong>Automatically sign me out after</Text>
          <div style={{ marginTop: 6 }}>
            <Select
              style={{ width: 280 }}
              disabled={!loaded}
              value={prefs.idleTimeoutMinutes}
              onChange={(v) => save({ ...prefs, idleTimeoutMinutes: Number(v) })}
              options={IDLE_TIMEOUT_CHOICES.map((m) => ({ value: m, label: idleLabel(m) }))}
            />
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            On a shared computer, pick a short timeout. Signing out or a security policy still ends the session.
          </Text>
        </div>
      </Space>
    </Card>
  );
};

const SettingsPage: React.FC = () => {
  const stored = JSON.parse(localStorage.getItem('admin_user') || '{}');
  const [logo, setLogo] = useState<string | undefined>(stored.tenantLogo);
  const [uploading, setUploading] = useState(false);

  // Refresh the current logo from the tenant record (covers existing tenants
  // whose logo was set elsewhere).
  useEffect(() => {
    if (stored.tenantSlug) {
      getTenantBranding(stored.tenantSlug).then((b) => { if (b?.logoUrl) setLogo(b.logoUrl); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      message.error('Please choose an image file.');
      return false;
    }
    setUploading(true);
    try {
      const raster = await toRasterLogo(file);
      const url = await uploadTenantLogo(raster);
      await saveTenantLogo(url);
      // Mirror to payroll so payslip PDFs pick up the logo (best-effort).
      try {
        await syncPayrollBrandingLogo(url);
      } catch {
        message.warning('Logo saved, but payslip branding could not sync.');
      }
      setLogo(`${url}?t=${Date.now()}`); // cache-bust
      message.success('Company logo updated.');
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Could not update the logo.');
    } finally {
      setUploading(false);
    }
    return false; // prevent antd's auto-upload
  };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <Title level={3}>Settings</Title>

      <Card title="Company branding">
        <Paragraph type="secondary">
          Your logo appears across the app for <Text strong>{stored.tenantName || 'your company'}</Text>.
          Changing it updates the existing company record.
        </Paragraph>
        <Space size="large" align="center">
          <div
            style={{
              width: 88, height: 88, borderRadius: 12, border: '1px solid #f0f0f0',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden', background: '#fafafa',
            }}
          >
            {logo
              ? <img src={logo} alt="Company logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              : <ShopOutlined style={{ fontSize: 32, color: '#f97316' }} />}
          </div>
          <div>
            <Upload accept="image/*" maxCount={1} beforeUpload={handleFile} showUploadList={false}>
              <Button icon={<UploadOutlined />} loading={uploading} type="primary">
                {logo ? 'Replace logo' : 'Upload logo'}
              </Button>
            </Upload>
            <div style={{ marginTop: 6 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>PNG, JPG or SVG. Max 5 MB.</Text>
            </div>
          </div>
        </Space>
      </Card>

      <SessionPrefsCard />
    </div>
  );
};

export default SettingsPage;
